import React, { useMemo } from "react";
import {
	formatPickupDateShort,
	formatPickupSlotLabel,
	isCollectionOrderType,
} from "../../../utils/collectionTimeSlots";
import "./CollectionTimeBanner.css";

const CLOCK_ICON = (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
		<path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const PICKUP_TIME_LABEL = "Pickup Time";

/**
 * Pickup time — site header pill or inline panel.
 */
export default function CollectionTimeBanner({
	orderType,
	pickupTime,
	pickupDate,
	openingHoursString = "",
	onChangeClick,
	className = "",
	compact = false,
	variant = "default",
}) {
	const isCollection = isCollectionOrderType(orderType);
	const hasTime = Boolean(String(pickupTime || "").trim());
	const isHeader = variant === "header";

	const slotLabel = useMemo(
		() =>
			formatPickupSlotLabel(pickupTime, {
				openingHoursString,
				pickupDateYmd: pickupDate,
			}),
		[pickupTime, pickupDate, openingHoursString]
	);
	const dateNote = useMemo(() => formatPickupDateShort(pickupDate), [pickupDate]);

	if (!isCollection) return null;

	const actionLabel = hasTime ? "Change" : "Choose time";
	const useCompactLine = compact || isHeader;
	const emptyPrompt = "Choose pickup time";

	return (
		<div
			className={`collection-time-banner ${useCompactLine ? "collection-time-banner--compact" : ""} ${isHeader ? "collection-time-banner--header" : ""} ${className}`.trim()}
			role="status"
			aria-live="polite"
		>
			<div className="collection-time-banner__main">
				<span className="collection-time-banner__icon" aria-hidden>
					{CLOCK_ICON}
				</span>
				{useCompactLine ? (
					<p className="collection-time-banner__line">
						{hasTime ? (
							<>
								<span className="collection-time-banner__line-prefix">{PICKUP_TIME_LABEL}</span>
								<strong className="collection-time-banner__slot">{slotLabel}</strong>
								{dateNote ? (
									<span className="collection-time-banner__date">{dateNote}</span>
								) : null}
							</>
						) : (
							<>
								<span className="collection-time-banner__line-prefix">{PICKUP_TIME_LABEL}</span>
								<span className="collection-time-banner__line-muted">{emptyPrompt}</span>
							</>
						)}
					</p>
				) : (
					<div className="collection-time-banner__copy">
						<span className="collection-time-banner__label">{PICKUP_TIME_LABEL}</span>
						<span
							className={`collection-time-banner__value ${hasTime ? "" : "collection-time-banner__value--muted"}`.trim()}
						>
							{hasTime ? (
								<>
									{slotLabel}
									{dateNote ? (
										<span className="collection-time-banner__date"> · {dateNote}</span>
									) : null}
								</>
							) : (
								emptyPrompt
							)}
						</span>
					</div>
				)}
			</div>
			{onChangeClick ? (
				<button
					type="button"
					className={
						!hasTime && (isHeader || compact)
							? "collection-time-banner__cta btn btn-sm fw-semibold"
							: "collection-time-banner__change btn btn-link"
					}
					onClick={onChangeClick}
				>
					{actionLabel}
				</button>
			) : null}
		</div>
	);
}
